import { defineAsyncComponent } from 'vue'
import type { RouteComponent, RouteRecordRaw } from 'vue-router'
import { isChunkLoadError, reloadForNewVersion } from './chunkReload'
import PilotageSectionError from '@/views/products/pid/referentials/pages/PilotageSectionError.vue'
import PilotageSectionSkeleton from '@/views/products/pid/referentials/pages/PilotageSectionSkeleton.vue'
import {
  PILOTAGE_SECTIONS,
  pilotageSectionRouteName,
  translateLegacyPilotage,
  type PilotageSection,
  type PilotageTrack,
} from '@/utils/pilotageRoutes'

type Lazy = () => Promise<RouteComponent | { default: RouteComponent }>

/**
 * MYAC-1522 3b step 5a — the Pilotage section pages, one per child route. Lazy:
 * a member who never opens Cohérence never downloads it.
 */
export const PILOTAGE_PAGE_OF_SECTION: Record<PilotageSection, Lazy> = {
  conversation: () => import('@/views/products/pid/referentials/pages/ConversationPage.vue'),
  standards: () => import('@/views/products/pid/referentials/pages/StandardsPage.vue'),
  essentials: () => import('@/views/products/pid/referentials/pages/EssentialsPage.vue'),
  compliance: () => import('@/views/products/pid/referentials/pages/CompliancePage.vue'),
  'action-plan': () => import('@/views/products/pid/referentials/pages/ActionPlanPage.vue'),
  consistency: () => import('@/views/products/pid/referentials/pages/ConsistencyPage.vue'),
  continuity: () => import('@/views/products/pid/referentials/pages/ContinuityPage.vue'),
  completeness: () => import('@/views/products/pid/referentials/pages/CompletenessPage.vue'),
}

/**
 * A section's code failed to download. A section is not a route-level lazy
 * component, so `router.onError` never sees it: retry once, then the same full
 * reload as the router (after a deploy the old chunk is gone; without this the
 * skeleton would stay). Any other error fails as usual.
 */
export function onSectionLoadError(error: Error, retry: () => void, fail: () => void, attempts: number): void {
  if (!isChunkLoadError(error)) return fail()
  if (attempts <= 1) return retry()
  // Already reloaded for this a moment ago: show the section's error, no loop.
  if (!reloadForNewVersion(error)) fail()
}

/**
 * A section as the router sees it: a component that is there at once, so a
 * click switches the page immediately (a lazy route component makes the router
 * wait for the download, the old page staying on screen); it shows the
 * section's skeleton until its code has arrived.
 */
export function sectionComponent(loader: Lazy): RouteComponent {
  return defineAsyncComponent({
    loader: loader as () => Promise<RouteComponent>,
    loadingComponent: PilotageSectionSkeleton,
    errorComponent: PilotageSectionError,
    delay: 0,
    // A section is no longer a route-level lazy component, so `router.onError`
    // never sees its download failing: retry once, then the same full reload
    // (after a deploy the old chunk is gone; without this the skeleton stays).
    onError: onSectionLoadError,
  })
}

/** Download every section's code in the background (the layout calls it once it is on screen). */
export function prefetchPilotagePages(): void {
  for (const load of Object.values(PILOTAGE_PAGE_OF_SECTION)) void load().catch(() => undefined)
}

/** The layout every section renders inside. */
const PILOTAGE_LAYOUT: Lazy = () => import('@/views/products/pid/referentials/ProductReferentialsView.vue')

/**
 * The Pilotage records of one track, to sit among the children of its
 * `/{track}/:pid` record — ONE definition for `src/router/index.ts` and the
 * Pilotage test harness, so the two cannot drift.
 *
 *  - `referentials` (`{track}-referentials`): the old single address, kept as
 *    the entry of the translation layer (`?tab=x` → the section's address,
 *    every other parameter kept; the e-mails' bare `/referentials` lands on the
 *    conversation).
 *  - one parent record (`{track}-pilotage`), path `''`, whose component is the
 *    layout. Named only so the router accepts an empty-path child under the
 *    named `:pid` record without a warning; nothing navigates to it, and a stray
 *    link to it lands on a section through the same translation as the legacy
 *    address (its `redirect`). Its `meta` is inherited by every
 *    child: the title, `ownsQuery` (the layout and its pages drive their own
 *    query) and `viewKey: 'pilotage'`, the key `HomeView` remounts on — shared
 *    by every section, so moving between them never remounts the layout.
 *    Path `''` beside the project's own `''` root: equal rank, and the root is
 *    declared first, so `/{track}/:pid` still resolves to the root (pinned in
 *    `routeActions.test.ts`).
 *  - one child per section (`{track}-pilotage-{section}`, `meta.section`),
 *    generated from `PILOTAGE_SECTION_OF_TAB`: the addresses are unchanged
 *    (`/qms/:pid/compliance`).
 *
 * `layout` is overridable for a test that wants a stand-in; the app passes nothing.
 */
export function pilotageRecords(track: PilotageTrack, layout: Lazy | RouteComponent = PILOTAGE_LAYOUT): RouteRecordRaw[] {
  return [
    {
      path: 'referentials',
      name: `${track}-referentials`,
      redirect: (to) => translateLegacyPilotage(to),
    },
    {
      path: '',
      name: `${track}-pilotage`,
      redirect: (to) => translateLegacyPilotage(to),
      component: layout,
      meta: {
        title: 'pageTitles.referentials',
        ownsQuery: true,
        viewKey: 'pilotage',
      },
      children: PILOTAGE_SECTIONS.map((section) => ({
        path: section,
        name: pilotageSectionRouteName(track, section),
        meta: { section },
        component: sectionComponent(PILOTAGE_PAGE_OF_SECTION[section]),
      })),
    },
  ]
}
